import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Modal,
} from "react-bootstrap";
import { FaTrashAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../BaseURL";
import ReusableTooltip from "./ReusableTooltip";
import "../Assets/WebsiteCss/comStyle.css";

function AllProjects() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/projects`);
      setProjects(res.data.data || []);
    } catch (err) {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;

    try {
      setLoading(true);
      await axios.delete(`${BASE_URL}/api/projects/${projectToDelete._id}`);
      setProjects((prev) => prev.filter((p) => p._id !== projectToDelete._id));
      toast.success("Project deleted successfully");
      setShowDeleteModal(false);
      setProjectToDelete(null);
    } catch (err) {
      toast.error("Failed to delete project");
    } finally {
      setLoading(false);
    }
  };

  // Filter projects based on search
  const filteredProjects = projects.filter((p) =>
    p.title.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Container className="mt-4 container-lg p-0">
      <Card>
        {/* HEADER */}
        <Card.Header className="d-flex justify-content-between align-items-center mainBGColor text-white fw-bold px-3 py-2">
          <span>All Projects</span>

          <Button
            className=""
            onClick={() => navigate("/dashboard/add-project")}
            style={{
              padding: "6px 14px",
              fontSize: "13px",
            }}
          >
            Add Project
          </Button>
        </Card.Header>

        <Card.Body>
          {/* SEARCH */}
          <Row className="g-2 mb-3">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Search by title"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ fontSize: "14px" }}
              />
            </Col>
          </Row>

          {/* GRID LAYOUT */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" /> Loading...
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-5 text-muted">
              --- No Projects Found ---
            </div>
          ) : (
            <Row className="g-3">
              {filteredProjects.map((project) => (
                <Col key={project._id} xs={6} sm={4} md={3} lg={2}>
                  <Card className="h-100 shadow-sm project-card">
                    {/* Project Image */}
                    <div
                      style={{
                        position: "relative",
                        paddingTop: "100%",
                        overflow: "hidden",
                        backgroundColor: "#f0f0f0",
                      }}
                    >
                      {project.images && project.images.length > 0 ? (
                        <img
                          src={`${BASE_URL}${project.images[0]}`}
                          alt={project.title}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#e9ecef",
                            color: "#6c757d",
                            fontSize: "12px",
                          }}
                        >
                          No Image
                        </div>
                      )}

                      {/* Delete Button Overlay */}
                      <div
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                        }}
                      >
                        <ReusableTooltip text="Delete">
                          <Button
                            onClick={() => handleDeleteClick(project)}
                            className="deleteicon"
                            size="sm"
                            style={{
                              padding: "6px 8px",
                              fontSize: "12px",
                              borderRadius: "4px",
                            }}
                          >
                            <FaTrashAlt size={12} />
                          </Button>
                        </ReusableTooltip>
                      </div>
                    </div>

                    {/* Project Info */}
                    <Card.Body className="p-2">
                      <h6
                        className="mb-1"
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={project.title}
                      >
                        {project.title}
                      </h6>
                      <p
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "11px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={project.category}
                      >
                        {project.category}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* DELETE CONFIRMATION MODAL */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Are you sure you want to delete the project "
          <strong>{projectToDelete?.title}</strong>"?
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="logout-cancel-btn"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>

          <Button className="logout-confirm-btn" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .project-card {
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
          cursor: pointer;
        }

        .project-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }
      `}</style>
    </Container>
  );
}

export default AllProjects;
